<script setup lang="ts">
// import EmblaAutoScrollPlugin from 'embla-carousel-auto-scroll'
import useEmblaCarousel from 'embla-carousel-vue'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'
import { Ticker } from 'motion-plus-vue'
import type { IntRange } from 'type-fest'

const { tm, rt } = useI18n()
const isScreenSM = useMediaQuery('(min-width: 768px)')

const [emblaRef, emblaApi] = useEmblaCarousel({
  align: 'start',
  slidesToScroll: 'auto',
  loop: true,
}, [
  WheelGesturesPlugin(),
])

const { selectedIndex, scrollSnaps, handleClick } = useEmblaDots(emblaApi)
const { isPrevDisabled, isNextDisabled, handlePrevious, handleNext } = useEmblaButtons(emblaApi)

function getStaticAsset(index: StringifiedInt<IntRange<0, 6>>) {
  const value = match(index)
    .with('0', () => 'kubu.jpg')
    .with('1', () => 'memories-bar-grill.jpg')
    .with('2', () => 'ristorante-luce.jpg')
    .with('3', () => 'trattoria-a-vucchella.jpg')
    .with('4', () => 'chubbys.jpg')
    .with('5', () => 'bullpen-bbq-taphouse.jpg')
    .exhaustive()

  return `${S3_ASSETS_URL}/about/carousel/${value}`
}
</script>

<template>
  <section>
    <Ticker
      v-if="isScreenSM"
      :gap="0"
      class="max-w-full pb-16 pt-8"
    >
      <div
        v-for="(item, itemIndex) in tm('about.section.text_testimonials.items')"
        :key="itemIndex"
        class="relative mr-5 inline-flex h-[32rem] w-[25rem] select-none items-end justify-center overflow-clip rounded-[1.875rem] bg-cover bg-center"
      >
        <UiImageWithOverlay
          :src="getStaticAsset(itemIndex)"
          class="size-full object-cover object-top"
          container-class="size-full"
          draggable="false"
          role="presentation"
        />

        <div class="absolute inset-x-0 bottom-0 text-balance p-5 text-xl text-white before:inline-block before:content-['“'] after:inline-block after:content-['”']">
          {{ item.comment }}
        </div>
      </div>
    </Ticker>

    <div
      v-else
      class="realtive flex flex-col gap-y-8 overflow-clip px-4 pb-8 md:px-8"
    >
      <div
        ref="emblaRef"
        class="relative select-none"
      >
        <div class="flex [backface-visibility:hidden]">
          <div
            v-for="(item, itemIndex) in tm('about.section.text_testimonials.items')"
            :key="itemIndex"
            class="relative ml-5 flex shrink-0 grow-0 basis-[calc(100%-1.25rem*2)] justify-stretch"
          >
            <div class="w-full cursor-move overflow-clip rounded-2xl border border-gray-200 bg-white shadow-xl shadow-black/5">
              <div class="relative flex max-h-[30rem] min-w-0 overflow-clip rounded-b-2xl">
                <UiImageWithOverlay
                  :src="getStaticAsset(itemIndex)"
                  class="size-full object-cover object-top"
                  draggable="false"
                  role="presentation"
                />
              </div>

              <div class="absolute inset-x-0 bottom-0 text-balance p-5 text-xl text-white before:inline-block before:content-['“'] after:inline-block after:content-['”']">
                {{ rt(item.comment) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <nav class="flex items-center justify-end gap-x-5">
        <div class="hidden gap-x-2 md:flex">
          <button
            v-for="(_, itemIndex) in scrollSnaps"
            :key="itemIndex"
            type="button"
            :class="['size-3 rounded-[50%]', selectedIndex === itemIndex ? 'bg-orange-500' : 'bg-gray-200']"
            @click="handleClick(itemIndex)"
          />
        </div>

        <div class="flex gap-x-2">
          <button
            type="button"
            class="inline-flex size-12 items-center justify-center rounded-lg border transition-all enabled:border-neutral-100 enabled:bg-white enabled:shadow-2xl disabled:border-gray-100 disabled:bg-gray-100 disabled:text-gray-400"
            :disabled="isPrevDisabled"
            @click="handlePrevious"
          >
            <UiIcon
              name="arrow-forward-rounded"
              class="-rotate-180"
            />
          </button>

          <button
            type="button"
            class="inline-flex size-12 items-center justify-center rounded-lg border transition-all enabled:border-neutral-100 enabled:bg-white enabled:shadow-2xl disabled:border-gray-100 disabled:bg-gray-100 disabled:text-gray-400"
            :disabled="isNextDisabled"
            @click="handleNext"
          >
            <UiIcon name="arrow-forward-rounded" />
          </button>
        </div>
      </nav>
    </div>
  </section>
</template>
